import { HelpCircle, X } from "lucide-react";
import { isValidElement, ReactNode, useState } from "react";
import { useFormContext } from "react-hook-form";
import { Button } from "@/components/ui/button";
import { FormLabel } from "@/components/ui/form";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";

type HelpDetailsObject = {
  title?: string;
  description?: string;
  example?: string;
  benefits?: string[];
};

interface SettingFieldProps {
  label: string;
  helpText?: string;
  helpDetails?: ReactNode | HelpDetailsObject;
  required?: boolean;
  disabled?: boolean;
  className?: string;
  children?: ReactNode;
}

export function SettingField({
  label,
  helpText,
  helpDetails,
  required = false,
  disabled = false,
  className,
  children,
}: SettingFieldProps) {
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);

  let formContext: ReturnType<typeof useFormContext> | null;
  try {
    // biome-ignore lint/correctness/useHookAtTopLevel: useFormContext throws when used outside FormProvider; try/catch is intentional to support both form and non-form contexts
    formContext = useFormContext();
  } catch {
    formContext = null;
  }

  const LabelComponent = formContext ? FormLabel : Label;

  // Check if device has touch capability
  const isTouchDevice = typeof window !== "undefined" && "ontouchstart" in window;

  const helpIcon = (
    <Button
      type="button"
      variant="ghost"
      size="sm"
      className={cn(
        "h-auto p-1 text-muted-foreground hover:text-foreground",
        // Larger tap target on mobile
        isTouchDevice && "min-h-[44px] min-w-[44px] -m-2",
        disabled && "opacity-50 cursor-not-allowed"
      )}
      disabled={disabled}
      onClick={() => setIsPopoverOpen(!isPopoverOpen)}
    >
      <HelpCircle className="h-4 w-4" />
      <span className="sr-only">Show help for {label}</span>
    </Button>
  );

  const helpContent = (
    <div className="max-w-sm space-y-3">
      {helpText && <div className="text-sm text-muted-foreground leading-relaxed">{helpText}</div>}

      {helpDetails && (
        <div className="text-sm text-muted-foreground">
          {isValidElement(helpDetails) || typeof helpDetails !== "object"
            ? (helpDetails as ReactNode)
            : (() => {
                const obj = helpDetails as HelpDetailsObject;
                const hasList = Array.isArray(obj.benefits) && obj.benefits.length > 0;
                return (
                  <div className="space-y-2">
                    {obj.title && (
                      <div className="text-sm font-medium text-foreground">{obj.title}</div>
                    )}
                    {obj.description && (
                      <div className="text-sm text-muted-foreground leading-relaxed">
                        {obj.description}
                      </div>
                    )}
                    {obj.example && (
                      <div className="text-sm leading-relaxed">
                        <span className="font-medium">Example:</span> {obj.example}
                      </div>
                    )}
                    {hasList && (
                      <ul className="text-sm space-y-1 text-muted-foreground">
                        {obj.benefits?.map((benefit) => (
                          <li key={benefit}>• {benefit}</li>
                        ))}
                      </ul>
                    )}
                  </div>
                );
              })()}
        </div>
      )}

      <Button
        variant="ghost"
        size="sm"
        className="ml-auto flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground p-1"
        onClick={() => setIsPopoverOpen(false)}
      >
        <X className="h-3 w-3" />
        Close
      </Button>
    </div>
  );

  if (!helpText && !helpDetails) {
    // No help content, render without help icon
    return (
      <div className={cn("space-y-2", className)}>
        <LabelComponent className="text-base font-medium">
          {label}
          {required && <span className="text-destructive ml-1">*</span>}
        </LabelComponent>
        {children !== undefined && <div>{children}</div>}
      </div>
    );
  }

  return (
    <div className={cn("space-y-2", className)}>
      <div className="flex items-center gap-2">
        <LabelComponent className="text-base font-medium">
          {label}
          {required && <span className="text-destructive ml-1">*</span>}
        </LabelComponent>

        {isTouchDevice ? (
          // Mobile: Popover only
          <Popover open={isPopoverOpen} onOpenChange={setIsPopoverOpen}>
            <PopoverTrigger asChild>{helpIcon}</PopoverTrigger>
            <PopoverContent className="w-80 p-4" align="start" side="bottom" sideOffset={4}>
              {helpContent}
            </PopoverContent>
          </Popover>
        ) : (
          // Desktop: Tooltip + clickable popover
          <TooltipProvider>
            <Tooltip>
              <Popover open={isPopoverOpen} onOpenChange={setIsPopoverOpen}>
                <PopoverTrigger asChild>
                  <TooltipTrigger asChild>{helpIcon}</TooltipTrigger>
                </PopoverTrigger>
                <PopoverContent className="w-80 p-4" align="start" side="bottom" sideOffset={4}>
                  {helpContent}
                </PopoverContent>
              </Popover>
              <TooltipContent side="top" className="max-w-xs">
                <p className="text-xs">Click for detailed help</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
      </div>

      {children !== undefined && <div>{children}</div>}
    </div>
  );
}
